import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
// import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { getUserLessons } from "../services/seasons/utils";
import {
  calculateCountdownTarget,
  extendWebinarAccess,
  getWebinarAccessStatus,
  getUserWebinars,
} from "../services/webinar";
import { AddComment } from "./comment";
import { Comments } from "./comments/Comments";
import DiscountCodeModal from "./gift-modal/DiscountCodeModal";
import GiftModal from "./gift-modal/GiftModal";
import TimeoutModal from "./gift-modal/TimeoutModal";
import { Header } from "./header";
import { LessonList } from "./lesson-lists";
import LineProgress from "./progress/Progressbar";

const Landing = () => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  // const location = useLocation();
  const [isGiftModal, setIsGiftModal] = useState(false);
  const [isDiscountCodeModal, setIsDisCountCodeModal] = useState(false);
  const [isTimeoutModal, setIsTimeoutModal] = useState(false);
  const [activeLessonId, setActiveLessonId] = useState<number | null>(() => {
    // Prefer previously selected lesson if available; otherwise wait for polling to pick the right one
    const saved = localStorage.getItem("activeChapterId");
    return saved ? Number(saved) : null;
  });

  const [countdownTarget, setCountdownTarget] = useState<Date | null>(null);
  const timeoutHandledRef = useRef(false);

  // Fetch user webinars
  const { data: userWebinars } = useQuery({
    queryKey: ["userWebinars"],
    queryFn: getUserWebinars,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const isFreeAccess = userWebinars?.some((w) => w.webinar.freeAccess) ?? false;

  const activeWebinarId = userWebinars?.[0]?.webinar.id;

  // Fetch access status (expired or not) from the backend
  const { data: accessStatus } = useQuery({
    queryKey: ["webinar-access-status", activeWebinarId],
    queryFn: () => getWebinarAccessStatus(activeWebinarId!),
    enabled: !!activeWebinarId,
  });

  // Fetch user lessons to compute progress
  const { data: userLessons } = useQuery({
    queryKey: ["user-lessons"],
    queryFn: getUserLessons,
  });

  // Initialize localStorage with lesson progress on mount
  useEffect(() => {
    if (isAuthenticated) {
      getUserLessons()
        .then((lessons) => {
          lessons.forEach((lesson) => {
            const key = `lesson_${lesson.id}`;
            const data = {
              maxVideoViewSec: lesson.user_lesson?.maxVideoViewSec || 0,
              lastVideoViewSec: lesson.user_lesson?.lastVideoViewSec || 0,
            };
            localStorage.setItem(key, JSON.stringify(data));
          });

          // Remove old vjs:lastWatched keys
          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith("vjs:lastWatched:")) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach((key) => localStorage.removeItem(key));
        })
        .catch((error) => {
          console.error("Failed to initialize lesson progress:", error);
        });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const deadline = userWebinars?.[0]?.deadline;

    if (deadline) {
      setCountdownTarget(calculateCountdownTarget(deadline));
    }

    // Show the timeout modal when the backend reports the access as expired
    if (accessStatus?.isExpired && !timeoutHandledRef.current) {
      timeoutHandledRef.current = true;
      setIsTimeoutModal(true);
    }
  }, [userWebinars, accessStatus]);

  // Scroll to video section after API requests complete
  useLayoutEffect(() => {
    if (userLessons !== undefined && userWebinars !== undefined) {
      setTimeout(() => {
        requestAnimationFrame(() => {
          const videoSection = document.getElementById("video-section");
          if (videoSection) {
            videoSection.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      }, 1000);
    }
  }, [userLessons, userWebinars]);

  const extendAccessMutation = useMutation({
    mutationFn: () =>
      extendWebinarAccess(userWebinars?.[0]?.webinar.id ?? 0),
    onSuccess: () => {
      timeoutHandledRef.current = true;
      setIsTimeoutModal(false);
      setCountdownTarget(null);
      queryClient.invalidateQueries({ queryKey: ["userWebinars"] });
      queryClient.invalidateQueries({
        queryKey: ["webinar-access-status", activeWebinarId],
      });
    },
  });

  return (
    <div>
      <Header showLogo={true} countdownTarget={countdownTarget} />
      <div>
        {(() => {
          const TOTAL_LESSONS = 43;
          const completed = Math.min(userLessons?.length ?? 0, TOTAL_LESSONS);
          const percent = Math.round((completed / TOTAL_LESSONS) * 100);
          return <LineProgress fillPercent={percent} duration={1.5} />;
        })()}
      </div>
      <div className="flex flex-col gap-4">
        <div
          className="h-[628px] md:h-[828px] flex flex-col justify-center"
          style={{
            background: "linear-gradient(to bottom, #FFFFFF, #A36AFF)",
          }}
        >
          <LessonList
            activeLessonId={activeLessonId}
            onLessonChange={setActiveLessonId}
            freeAccess={isFreeAccess}
          />
        </div>
        <div className="mt-72 md:mt-[450px]">
          <AddComment lessonId={activeLessonId} />
        </div>
        <div
          className="mt-16 flex flex-col items-center justify-between"
          style={{ background: "linear-gradient(to left, #57C5F9, #A36AFF)" }}
        >
          <h3 className="font-kalameh text-[40px] text-white mt-20">
            نظرات دانش آموختگان
          </h3>
          <Comments lessonId={activeLessonId} />
          {/* <p className="text-xl font-kalameh text-white mb-6">
            مشاهده همه نظرات
          </p> */}
          <img
            src="/images/isolation_mode2.png"
            alt="isolation"
            width={367}
            height={83}
          />
        </div>
      </div>
      {isGiftModal && <GiftModal onClose={() => setIsGiftModal(false)} />}
      {isDiscountCodeModal && (
        <DiscountCodeModal onClose={() => setIsDisCountCodeModal(false)} />
      )}
      {isTimeoutModal && (
        <TimeoutModal
          onClose={() => setIsTimeoutModal(false)}
          onExtend={() => extendAccessMutation.mutate()}
          isExtending={extendAccessMutation.isPending}
        />
      )}
    </div>
  );
};

export default Landing;
